import { TableLazyLoadEvent } from 'primeng/table';
import { PAGINATION_CONSTANTS } from '../constants/pagination.constants';
import { FilterMatchMode, FilterParams, LazyLoadParams, PaginationParams, SortParams } from '../types/lazy-load.interface';

export class LazyLoadConverter {
  static fromTableEvent(event: TableLazyLoadEvent, globalFilterFields?: string[]): LazyLoadParams {
    const pagination: PaginationParams = {
      page: Math.floor((event.first ?? 0) / (event.rows ?? PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE)) + 1,
      pageSize: event.rows ?? PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
    };
    let sorts: SortParams[] = [];

    if (event.multiSortMeta && event.multiSortMeta.length > 0) {
      sorts = event.multiSortMeta.map(s => ({
        field: s.field,
        order: s.order === 1 ? 'asc' : 'desc',
      }));
    } else if (event.sortField) {
      const sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
      if (sortField) {
        sorts = [
          {
            field: sortField,
            order: event.sortOrder === 1 ? 'asc' : 'desc',
          },
        ];
      }
    }

    const filters: FilterParams[] = [];

    if (event.filters) {
      Object.entries(event.filters).forEach(([field, filterMeta]) => {
        if (Array.isArray(filterMeta)) {
          filterMeta.forEach(meta => {
            if (this.isValidFilterValue(meta.value)) {
              filters.push({
                field,
                value: meta.value,
                matchMode: (meta.matchMode as FilterMatchMode) || 'contains',
              });
            }
          });
        } else {
          if (filterMeta && this.isValidFilterValue(filterMeta.value)) {
            filters.push({
              field,
              value: filterMeta.value,
              matchMode: (filterMeta.matchMode as FilterMatchMode) || 'contains',
            });
          }
        }
      });
    }
    const globalFilter = event.globalFilter
      ? Array.isArray(event.globalFilter)
        ? event.globalFilter[0]
        : event.globalFilter
      : undefined;

    return {
      pagination,
      ...(sorts.length > 0 && { sorts }),
      ...(filters.length > 0 && { filters }),
      ...(globalFilter !== undefined && { globalFilter }),
      ...(globalFilterFields !== undefined && { globalFilterFields }),
    };
  }

  private static isValidFilterValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  }

  static areParamsEqual(a?: LazyLoadParams, b?: LazyLoadParams): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;

    // Compare pagination first (most likely to differ, early exit)
    if (a.pagination.page !== b.pagination.page || a.pagination.pageSize !== b.pagination.pageSize) {
      return false;
    }

    // Compare sorts
    if (!this.arraysEqual(a.sorts, b.sorts)) return false;

    // Compare filters
    if (!this.arraysEqual(a.filters, b.filters)) return false;

    // Compare global filter
    if (a.globalFilter !== b.globalFilter) return false;

    // Compare globalFilterFields
    if (!this.arraysEqual(a.globalFilterFields, b.globalFilterFields)) return false;

    return true;
  }

  private static arraysEqual<T>(a?: T[], b?: T[]): boolean {
    if (!a && !b) return true;
    if (!a || a.length !== b?.length) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  }
}
